import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Mail, Share2, Copy,
  Linkedin, Facebook, MessageCircle,
  Twitter, Send, Instagram, Music,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";

/**
 * IconTile — small dark-glass tile with brand-color icon ring.
 * Exported so callers can drop in extra share targets if needed.
 */
export const IconTile = ({ icon: Icon, label, color, onClick, testId, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="flex flex-col items-center justify-center gap-1 rounded-lg p-2.5 transition-all hover:scale-[1.03] hover:bg-white/5 disabled:opacity-50 disabled:hover:scale-100"
    style={{
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.10)",
    }}
    title={label}
  >
    <span
      className="inline-flex items-center justify-center rounded-full w-9 h-9"
      style={{ background: `${color}1A`, border: `1px solid ${color}55` }}
    >
      <Icon className="w-4 h-4" style={{ color }} />
    </span>
    <span className="text-[10px] font-medium" style={{ color: "var(--color-text-secondary, #94a3b8)" }}>
      {label}
    </span>
  </button>
);

/**
 * SocialShareGrid — single source of truth for the rich invite/share surface.
 *
 * Layout (top → bottom):
 *   1. Link readout + Copy button       (toggle: showLinkReadout)
 *   2. "Share via…" native sheet button (toggle: showNativeShare; capability-gated)
 *   3. 3×3 grid: WhatsApp · Messenger · Telegram · LinkedIn · Facebook ·
 *                X (Twitter) · Instagram · TikTok · Email
 *
 * Props:
 *   url             - the URL to share (required)
 *   message         - prefilled text for WhatsApp/Email (defaults to the URL alone)
 *   shortText       - prefilled text for Twitter/Telegram (chars are at a premium there)
 *   emailSubject    - subject line for the mailto: intent
 *   nativeTitle     - title field passed to navigator.share()
 *   testIdPrefix    - prefix for data-testid attributes (default: "share")
 *   showLinkReadout - render the readonly URL row + Copy button (default: true)
 *   showNativeShare - render the OS-level "Share via…" pill (default: true; capability-gated)
 *
 * The component pulls i18n keys from the `network.*` namespace so it shows
 * consistent labels everywhere it's used.
 */
const SocialShareGrid = ({
  url,
  message,
  shortText,
  emailSubject,
  nativeTitle,
  testIdPrefix = "share",
  showLinkReadout = true,
  showNativeShare = true,
}) => {
  const { t } = useTranslation();
  const [nativeSupported, setNativeSupported] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      setNativeSupported(true);
    }
  }, []);

  const safeMessage = message || url || "";
  const safeShort = shortText || safeMessage;
  const safeSubject = emailSubject
    || t("network.shareEmailSubject", { firstName: "Someone", defaultValue: "An invite for you on Evoluras" });
  const safeNativeTitle = nativeTitle || safeSubject;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("network.shareCopied", { defaultValue: "Link copied!" }));
    } catch {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      toast.success(t("network.shareCopied", { defaultValue: "Link copied!" }));
    }
  };

  const open = async (platform) => {
    if (!url) return;
    if (platform === "native") {
      try {
        if (typeof navigator !== "undefined" && navigator.share) {
          await navigator.share({ title: safeNativeTitle, text: safeMessage, url });
          return;
        }
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
      await copy();
      return;
    }
    const eUrl = encodeURIComponent(url);
    const eMsg = encodeURIComponent(safeMessage);
    const eShort = encodeURIComponent(safeShort);
    const eSubject = encodeURIComponent(safeSubject);
    switch (platform) {
      case "whatsapp":
        window.open(`https://wa.me/?text=${eMsg}`, "_blank"); return;
      case "telegram":
        window.open(`https://t.me/share/url?url=${eUrl}&text=${eShort}`, "_blank"); return;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?url=${eUrl}&text=${eShort}`, "_blank", "width=600,height=500"); return;
      case "linkedin":
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${eUrl}`, "_blank", "width=600,height=500"); return;
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${eUrl}`, "_blank", "width=600,height=500"); return;
      case "messenger":
        // Messenger has no public share URL without an FB App ID — copy the
        // link and open the standard FB sharer as the closest fallback.
        await copy();
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${eUrl}`, "_blank", "width=600,height=500"); return;
      case "email":
        window.location.href = `mailto:?subject=${eSubject}&body=${eMsg}`; return;
      case "instagram":
        await copy();
        toast(t("network.sharePasteHint", { platform: "Instagram", defaultValue: "Link copied — paste it in Instagram DM" }));
        window.open("https://www.instagram.com/direct/inbox/", "_blank"); return;
      case "tiktok":
        await copy();
        toast(t("network.sharePasteHint", { platform: "TikTok", defaultValue: "Link copied — paste it in TikTok DM" }));
        window.open("https://www.tiktok.com/messages", "_blank"); return;
      default:
        await copy(); return;
    }
  };

  const disabled = !url;

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-grid`}>
      {showLinkReadout && (
        <div
          className="flex items-center gap-2 p-3 rounded-lg"
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <input
            readOnly
            value={url || t("network.generatingInviteLink", { defaultValue: "Generating link…" })}
            className="flex-1 bg-transparent text-sm outline-none truncate"
            style={{ color: "var(--color-text-primary)" }}
            data-testid={`${testIdPrefix}-link-input`}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={copy}
            disabled={disabled}
            className="flex-shrink-0"
            data-testid={`${testIdPrefix}-copy`}
          >
            <Copy className="w-4 h-4 mr-1" />
            {t("network.shareCopy", { defaultValue: "Copy" })}
          </Button>
        </div>
      )}

      {showNativeShare && nativeSupported && (
        <Button
          onClick={() => open("native")}
          disabled={disabled}
          className="w-full rounded-full"
          style={{
            background: "var(--color-brand-primary)",
            color: "var(--color-text-on-brand)",
          }}
          data-testid={`${testIdPrefix}-native`}
        >
          <Share2 className="w-4 h-4 mr-2" />
          {t("network.shareViaApps", { defaultValue: "Share via…" })}
        </Button>
      )}

      <div className="grid grid-cols-3 gap-2">
        <IconTile label="WhatsApp"    color="#25D366" icon={MessageCircle} disabled={disabled} onClick={() => open("whatsapp")}  testId={`${testIdPrefix}-whatsapp`} />
        <IconTile label="Messenger"   color="#0084FF" icon={MessageCircle} disabled={disabled} onClick={() => open("messenger")} testId={`${testIdPrefix}-messenger`} />
        <IconTile label="Telegram"    color="#26A5E4" icon={Send}          disabled={disabled} onClick={() => open("telegram")}  testId={`${testIdPrefix}-telegram`} />
        <IconTile label="LinkedIn"    color="#0A66C2" icon={Linkedin}      disabled={disabled} onClick={() => open("linkedin")}  testId={`${testIdPrefix}-linkedin`} />
        <IconTile label="Facebook"    color="#1877F2" icon={Facebook}      disabled={disabled} onClick={() => open("facebook")}  testId={`${testIdPrefix}-facebook`} />
        <IconTile label="X (Twitter)" color="#1DA1F2" icon={Twitter}       disabled={disabled} onClick={() => open("twitter")}   testId={`${testIdPrefix}-twitter`} />
        <IconTile label="Instagram"   color="#E4405F" icon={Instagram}     disabled={disabled} onClick={() => open("instagram")} testId={`${testIdPrefix}-instagram`} />
        <IconTile label="TikTok"      color="#FF0050" icon={Music}         disabled={disabled} onClick={() => open("tiktok")}    testId={`${testIdPrefix}-tiktok`} />
        <IconTile
          label={t("network.shareEmailLabel", { defaultValue: "Email" })}
          color="#64748b"
          icon={Mail}
          disabled={disabled}
          onClick={() => open("email")}
          testId={`${testIdPrefix}-email`}
        />
      </div>
    </div>
  );
};

export default SocialShareGrid;
